import { useEffect, useMemo, useRef, useState } from 'react';
import { X, Download, FileText, Film, Image as ImageIcon, FileWarning, Loader2 } from 'lucide-react';

interface FilePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileName: string;
  fileType: string;
  fileUrl: string;
}

type FileCategory = 'image' | 'video' | 'pdf' | 'unknown';

function getExtension(fileName: string) {
  const parts = fileName?.split('.');
  return parts?.length > 1 ? parts.pop()!.toLowerCase() : '';
}

function getFileCategory(fileType: string, fileName: string): FileCategory {
  const normalizedType = (fileType || '').toLowerCase();
  const ext = getExtension(fileName);

  if (normalizedType.startsWith('image/')) return 'image';
  if (normalizedType.startsWith('video/')) return 'video';
  if (normalizedType === 'application/pdf') return 'pdf';

  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
  if (['mp4', 'webm', 'ogg', 'mov', 'm4v'].includes(ext)) return 'video';
  if (ext === 'pdf') return 'pdf';

  return 'unknown';
}

export default function FilePreviewModal({
  isOpen,
  onClose,
  fileName,
  fileType,
  fileUrl,
}: FilePreviewModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [pdfFailed, setPdfFailed] = useState(false);
  const [mediaFailed, setMediaFailed] = useState(false);

  const category = useMemo(() => getFileCategory(fileType, fileName), [fileType, fileName]);

  useEffect(() => {
    if (!isOpen) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyTouchAction = document.body.style.touchAction;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
    window.addEventListener('keydown', handleEsc);

    const timer = window.setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 0);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.touchAction = previousBodyTouchAction;
      window.removeEventListener('keydown', handleEsc);
      window.clearTimeout(timer);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      setPdfFailed(false);
      setMediaFailed(false);
    }
  }, [isOpen, fileUrl, fileName, fileType]);

  if (!isOpen) return null;

  const renderContent = () => {
    if (!fileUrl) {
      return (
        <FallbackBlock
          title="Arquivo indisponível"
          description="Não foi possível localizar a URL do arquivo para visualização."
          typeLabel={fileType || 'Tipo desconhecido'}
        />
      );
    }

    if (category === 'image') {
      return (
        <div className="w-full min-h-full flex items-start justify-center p-4 md:p-6">
          <div className="relative inline-block max-w-full">
            {isLoading && (
              <LoadingOverlay label="Carregando imagem..." />
            )}
            {!mediaFailed ? (
              <img
                src={fileUrl}
                alt={fileName}
                onLoad={() => setIsLoading(false)}
                onError={() => {
                  setIsLoading(false);
                  setMediaFailed(true);
                }}
                className="block max-w-full h-auto rounded-xl shadow-sm border border-slate-200 bg-white"
              />
            ) : (
              <FallbackBlock
                title="Não foi possível carregar a imagem"
                description="Você ainda pode baixar o arquivo para abrir localmente."
                typeLabel={fileType || 'Imagem'}
              />
            )}
          </div>
        </div>
      );
    }

    if (category === 'video') {
      return (
        <div className="w-full min-h-full flex items-center justify-center p-4 md:p-6">
          <div className="w-full max-w-5xl">
            {isLoading && <LoadingOverlay label="Carregando vídeo..." />}
            {!mediaFailed ? (
              <video
                src={fileUrl}
                controls
                preload="metadata"
                onLoadedData={() => setIsLoading(false)}
                onError={() => {
                  setIsLoading(false);
                  setMediaFailed(true);
                }}
                className="w-full max-h-[70dvh] rounded-xl shadow-sm border border-slate-200 bg-black"
              >
                Seu navegador não suporta a reprodução de vídeo.
              </video>
            ) : (
              <FallbackBlock
                title="Não foi possível carregar o vídeo"
                description="Você ainda pode baixar o arquivo para visualizar no seu dispositivo."
                typeLabel={fileType || 'Vídeo'}
              />
            )}
          </div>
        </div>
      );
    }

    if (category === 'pdf') {
      return (
        <div className="w-full h-full min-h-[60dvh] p-3 md:p-4">
          {isLoading && <LoadingOverlay label="Carregando PDF..." />}
          {!pdfFailed ? (
            <iframe
              src={`${fileUrl}#toolbar=1&navpanes=0&scrollbar=1`}
              title={fileName}
              onLoad={() => setIsLoading(false)}
              className="w-full h-[72dvh] rounded-xl border border-slate-200 bg-white"
            />
          ) : (
            <FallbackBlock
              title="Visualização de PDF indisponível"
              description="Alguns navegadores ou servidores bloqueiam o preview embutido. Você ainda pode baixar o arquivo."
              typeLabel={fileType || 'PDF'}
            />
          )}

          {/* fallback silencioso caso o iframe trave sem carregar */}
          <img
            src=""
            alt=""
            className="hidden"
            onError={() => {
              // noop
            }}
          />
        </div>
      );
    }

    return (
      <FallbackBlock
        title="Visualização não disponível para este tipo de arquivo"
        description="Esse formato não possui preview embutido no modal."
        typeLabel={fileType || 'Tipo desconhecido'}
      />
    );
  };

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[70]"
      onMouseDown={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={`Visualizar: ${fileName}`}
    >
      <div className="absolute inset-0 bg-slate-900/75 backdrop-blur-sm" />

      <div className="absolute inset-0 p-2 sm:p-4 md:p-6">
        <div className="relative mx-auto flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
          <div className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur shrink-0">
            <div className="flex min-w-0 items-center gap-2">
              {category === 'image' && <ImageIcon size={16} className="text-brand-500 shrink-0" />}
              {category === 'video' && <Film size={16} className="text-brand-500 shrink-0" />}
              {category === 'pdf' && <FileText size={16} className="text-brand-500 shrink-0" />}
              {category === 'unknown' && <FileWarning size={16} className="text-amber-500 shrink-0" />}

              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-700">{fileName}</p>
                <p className="truncate text-xs text-slate-400">{fileType || 'Tipo não informado'}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              {fileUrl && (
                <a
                  href={fileUrl}
                  download={fileName}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-brand-50 hover:text-brand-600 transition-colors"
                  aria-label="Baixar arquivo"
                >
                  <Download size={18} />
                </a>
              )}

              <button
                ref={closeButtonRef}
                onClick={onClose}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                aria-label="Fechar"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="relative flex-1 overflow-auto bg-slate-50">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
}

function LoadingOverlay({ label }: { label: string }) {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/70 backdrop-blur-[1px]">
      <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 shadow-sm">
        <Loader2 size={16} className="animate-spin" />
        {label}
      </div>
    </div>
  );
}

function FallbackBlock({
  title,
  description,
  typeLabel,
}: {
  title: string;
  description: string;
  typeLabel: string;
}) {
  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
        <FileText size={42} className="mx-auto mb-3 text-slate-300" />
        <p className="text-sm font-semibold text-slate-700">{title}</p>
        <p className="mt-2 text-sm text-slate-500">{description}</p>
        <p className="mt-3 text-xs text-slate-400">{typeLabel}</p>
      </div>
    </div>
  );
}